// SignupPage.js
import React, { useState } from 'react';
import {
    Container, Typography, TextField, Button, CssBaseline,
    Grid, Link, Box, CircularProgress,
    FormControl,
    Select,
    MenuItem,
    InputLabel
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { addUser, setPassword } from '../Queries';
import VerificationForm from './VerificationForm';

const useStyles = makeStyles({
    root: {
        display: 'flex', minHeight: '100vh', justifyContent: 'center',
        alignItems: 'center', background: 'linear-gradient(to right, #0a0068, #cc4049)',
    },
    formContainer: {
        background: '#fff', padding: '40px', borderRadius: '8px',
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)', maxWidth: '600px',
    },
    submit: {
        marginTop: '18px', backgroundColor: '#0a0068', color: '#fff', width: '50%',
    },
    buttonWrapper: {
        display: 'flex', justifyContent: 'center', marginTop: '16px',
    },
    errorMessage: {
        color: 'red', textAlign: 'center', marginTop: '10px'
    }
});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignupPage = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '', firstName: '', lastName: '', user_role: 'User', designation: '', experience_level: '', company_code: ''
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const { email, firstName, lastName, designation, experience_level } = formData;
        if (!email || !firstName || !lastName || !designation || !experience_level) {
            setErrorMessage('All fields are required.');
            return false;
        }
        if (!emailRegex.test(email)) {
            setErrorMessage('Please enter a valid email address.');
            return false;
        }
        return true;
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setLoading(true);
        if (!validateForm()) {
            setLoading(false);
            return;
        }
        try {
            await Auth.signUp({
                username: formData.email,
                password: formData.password,
                attributes: { email: formData.email }
            });
            await addUser(formData, false);
            localStorage.setItem('confirmEmail', formData.email);
            setAwaitingConfirmation(true);
        } catch (error) {
            setErrorMessage(error.message || 'Signup failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmCode = async (code, emailParam, password) => {
        setLoading(true);
        try {
            await Auth.confirmSignUp(emailParam, code);
            await setPassword(emailParam, password);
            alert('Signup complete! You can now log in.');
            localStorage.removeItem('confirmEmail');
            navigate('/');
        } catch (error) {
            setErrorMessage(error.message || 'Confirmation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        try {
            await Auth.resendSignUp(formData.email);
            alert('Verification code resent to your email.');
        } catch (error) {
            setErrorMessage(error.message || 'Failed to resend code');
        }
    };

    return (
        <div className={classes.root}>
            <CssBaseline />
            <Container className={classes.formContainer}>
                {!awaitingConfirmation ? (
                    <>
                        <Typography component="h1" variant="h5" align="center" gutterBottom>
                            <b>Create an Account</b>
                        </Typography>
                        <br />
                        <form onSubmit={handleSignup}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} required />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} required />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Email" name="email" value={formData.email} onChange={handleChange} required />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Designation" name="designation" value={formData.designation} onChange={handleChange} required />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel id="course-label">Level of experience *</InputLabel>
                                        <Select
                                            labelId="experience_level-label"
                                            label="Level of experience"
                                            name="experience_level"
                                            // multiple
                                            value={formData.experience_level}
                                            onChange={handleChange}
                                            required
                                            renderValue={(selected) => (
                                                <div style={{ textAlign: "left" }}>{selected}</div>
                                            )}
                                        >
                                            <MenuItem value="" disabled>
                                                Select Level of Experience
                                            </MenuItem>
                                            <MenuItem value="0-2">0 - 2 Years</MenuItem>
                                            <MenuItem value="3-5">3 - 5 Years</MenuItem>
                                            <MenuItem value="6+">6+ Years</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Company Code" name="company_code" value={formData.company_code} onChange={handleChange} />
                                </Grid>
                            </Grid>
                            {errorMessage && <Typography className={classes.errorMessage}>{errorMessage}</Typography>}
                            <Box className={classes.buttonWrapper}>
                                <Button type="submit" variant="contained" className={classes.submit} disabled={loading}>
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
                                </Button>
                            </Box>
                            <Grid container justifyContent="center" style={{ marginTop: '10px' }}>
                                <Grid item>
                                    <Link href="/" variant="body2">
                                        Already have an account? Sign in
                                    </Link>
                                </Grid>
                            </Grid>
                        </form>
                    </>
                ) : (
                    <>
                        <Typography component="h1" variant="h5" align="center" gutterBottom>
                            Confirm Email & Set Password
                        </Typography>
                        <VerificationForm
                            email={formData.email}
                            onConfirm={handleConfirmCode}
                            onResend={handleResendCode}
                            errorMessage={errorMessage}
                            requirePassword={true}
                            loading={loading}
                        />
                    </>
                )}
            </Container>
        </div>
    );
};

export default SignupPage;
